import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, User as UserIcon, Loader2, ArrowRight, ShieldCheck } from 'lucide-react';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [requires2FA, setRequires2FA] = useState(false);
    const [userId, setUserId] = useState(null);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { login, verify2fa } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);
        try {
            if (requires2FA) {
                await verify2fa(userId, twoFactorCode);
                navigate('/');
            } else {
                const res = await login(username, password);
                if (res && res.require_2fa) {
                    setRequires2FA(true);
                    setUserId(res.user_id);
                } else {
                    navigate('/');
                }
            }
        } catch (err) {
            setError(requires2FA ? 'Неверный или просроченный код' : 'Неверный логин или пароль');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-[#0a0a0a] relative overflow-hidden">
            {/* Ambient Background Effects */}
            <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[120px] animate-pulse-slow"></div>
            <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-[120px] animate-pulse-slow delay-1000"></div>

            <div className="w-full max-w-md p-6 relative z-10">
                <div className="bg-neutral-900/60 backdrop-blur-2xl p-8 rounded-3xl border border-white/5 shadow-2xl shadow-black/50 overflow-hidden group">
                    {/* Decorative shine effect */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

                    <div className="text-center mb-10 relative">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500/20 to-purple-500/20 text-blue-400 rounded-3xl mb-6 ring-1 ring-white/10 shadow-lg group-hover:scale-105 transition-transform duration-500">
                            {requires2FA ? <ShieldCheck className="w-10 h-10 text-emerald-400" /> : <Lock className="w-10 h-10" />}
                        </div>
                        <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-neutral-400 tracking-tight mb-2">MCClub ERP</h1>
                        <p className="text-neutral-500 font-medium text-sm">
                            {requires2FA ? "Введите код из Telegram бота" : "Финансовая система нового поколения"}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6 relative">
                        {!requires2FA ? (
                            <>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-neutral-400 uppercase tracking-wider ml-1">Логин</label>
                                    <div className="relative group/input">
                                        <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500 group-focus-within/input:text-blue-500 transition-colors" />
                                        <input
                                            id="username"
                                            name="username"
                                            type="text"
                                            autoComplete="username"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            className="w-full bg-neutral-950/50 border border-neutral-800 text-white pl-12 pr-4 py-4 rounded-2xl focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:text-neutral-700"
                                            placeholder="Введите имя пользователя"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-neutral-400 uppercase tracking-wider ml-1">Пароль</label>
                                    <div className="relative group/input">
                                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500 group-focus-within/input:text-blue-500 transition-colors" />
                                        <input
                                            id="password"
                                            name="password"
                                            type="password"
                                            autoComplete="current-password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full bg-neutral-950/50 border border-neutral-800 text-white pl-12 pr-4 py-4 rounded-2xl focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:text-neutral-700"
                                            placeholder="Введите пароль"
                                            required
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4">
                                <label className="text-xs font-bold text-neutral-400 uppercase tracking-wider ml-1 text-center block">Код авторизации (2FA)</label>
                                <div className="relative group/input">
                                    <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500 transition-colors" />
                                    <input
                                        type="text"
                                        value={twoFactorCode}
                                        onChange={(e) => setTwoFactorCode(e.target.value)}
                                        className="w-full bg-neutral-950/50 border border-emerald-500/30 text-emerald-400 font-mono text-center text-xl tracking-[0.5em] pl-12 pr-4 py-4 rounded-2xl focus:border-emerald-500/80 focus:ring-4 focus:ring-emerald-500/20 outline-none transition-all placeholder:text-neutral-700 placeholder:tracking-normal"
                                        placeholder="------"
                                        maxLength={6}
                                        autoFocus
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="bg-red-500/10 border border-red-500/20 text-red-500 px-4 py-3 rounded-2xl text-sm font-medium flex items-center justify-center animate-in fade-in slide-in-from-top-2">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`w-full text-white font-bold py-4 rounded-2xl transition-all shadow-lg active:scale-[0.98] flex items-center justify-center gap-2 group/btn ${requires2FA ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-emerald-500/25' : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-blue-500/25'} disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    <span>{requires2FA ? 'Проверка...' : 'Вход...'}</span>
                                </>
                            ) : (
                                <>
                                    <span>{requires2FA ? 'Подтвердить вход' : 'Войти в систему'}</span>
                                    <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                    </form>
                </div>

                <p className="text-center text-neutral-600 text-xs mt-8">
                    &copy; 2026 MCClub ERP. Все права защищены.
                </p>
            </div>
        </div>
    );
};

export default Login;
